/* ------------------------------------------------------------------ *
 * lib/leavePdf.ts
 *
 * A5 leave application form — printed and taken to the Head of School
 * for a manual signature, then the HR officer records approval in-app.
 * Shows the leave details, current leave balances, and a short history
 * of the employee's previous applications.
 *
 * Kept display-string in/out (no academic-side type coupling): the
 * caller maps codes to labels and passes plain data.
 * ------------------------------------------------------------------ */
import jsPDF from 'jspdf';
import { SCHOOL } from '@/lib/school.config';

export type LeaveFormData = {
  staffName: string;
  jobTitle?: string;
  leave: {
    typeLabel: string;
    range: string;
    days: number;
    reason?: string;
    status: string;
    submitted?: string;
  };
  balances: { label: string; entitlement: number; taken: number; remaining: number }[];
  history: { typeLabel: string; range: string; days: number; status: string }[];
  date: string;
};

export function buildLeaveFormPdf(d: LeaveFormData, logo?: string | null): jsPDF {
  // A5 portrait: 148 × 210 mm.
  const pdf = new jsPDF({ unit: 'mm', format: 'a5' });
  const MX = 12;
  const right = 148 - MX;
  const NAVY: [number, number, number] = [27, 58, 107];
  const INK: [number, number, number] = [31, 41, 55];
  const MUTED: [number, number, number] = [107, 114, 128];
  const RULE: [number, number, number] = [209, 213, 219];
  const setT = (c: [number, number, number]) => pdf.setTextColor(c[0], c[1], c[2]);
  const setD = (c: [number, number, number]) => pdf.setDrawColor(c[0], c[1], c[2]);
  const setF = (c: [number, number, number]) => pdf.setFillColor(c[0], c[1], c[2]);
  let y = 12;

  // --- letterhead ---
  if (logo) { try { pdf.addImage(logo, 'PNG', MX, y - 1, 12, 12); } catch { /* ignore */ } }
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
  pdf.text(SCHOOL.name, MX + 15, y + 3);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7); setT(MUTED);
  pdf.text(`${SCHOOL.contact.phonePrimary}  ·  ${SCHOOL.contact.email}`, MX + 15, y + 7.5);
  y += 15;
  setD(RULE); pdf.setLineWidth(0.3); pdf.line(MX, y, right, y); y += 6;

  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12);
  pdf.text('LEAVE APPLICATION FORM', MX, y);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5); setT(MUTED);
  pdf.text(d.date, right, y, { align: 'right' });
  y += 7;

  // --- applicant ---
  setT(INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
  pdf.text(d.staffName, MX, y);
  if (d.jobTitle) {
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
    pdf.text(d.jobTitle, right, y, { align: 'right' });
  }
  y += 6;

  // --- leave details ---
  const detail = (k: string, v: string) => {
    setT(MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7);
    pdf.text(k.toUpperCase(), MX, y);
    setT(INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
    pdf.text(v, MX + 28, y);
    y += 5.4;
  };
  detail('Type', d.leave.typeLabel);
  detail('Dates', d.leave.range);
  detail('Working days', String(d.leave.days));
  detail('Status', d.leave.status);
  if (d.leave.submitted) detail('Submitted', d.leave.submitted);
  if (d.leave.reason) {
    setT(MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7);
    pdf.text('REASON', MX, y); y += 4;
    setT(INK); pdf.setFontSize(8.5);
    const lines = pdf.splitTextToSize(d.leave.reason, right - MX) as string[];
    lines.slice(0, 3).forEach((ln) => { pdf.text(ln, MX, y); y += 4; });
  }
  y += 2;

  // --- balances ---
  const miniTable = (
    title: string,
    cols: { label: string; x: number; align?: 'left' | 'right' }[],
    rows: string[][],
    emptyNote: string,
  ) => {
    setD(RULE); pdf.line(MX, y, right, y); y += 5;
    setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8);
    pdf.text(title, MX, y); y += 5;
    if (rows.length === 0) {
      setT(MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5);
      pdf.text(emptyNote, MX, y); y += 5; return;
    }
    setF(NAVY); pdf.rect(MX, y - 3.4, right - MX, 5, 'F');
    pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(6.5);
    cols.forEach((c) => pdf.text(c.label.toUpperCase(), c.x, y, c.align === 'right' ? { align: 'right' } : undefined));
    y += 4.6;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5);
    for (const r of rows) {
      if (y > 200) { pdf.addPage(); y = 14; }
      setT(INK);
      r.forEach((cell, i) => {
        const c = cols[i];
        pdf.text(cell, c.x, y, c.align === 'right' ? { align: 'right' } : undefined);
      });
      y += 4.6;
    }
    y += 1;
  };

  miniTable(
    'Leave balances',
    [
      { label: 'Type', x: MX },
      { label: 'Entitled', x: right - 44, align: 'right' },
      { label: 'Taken', x: right - 22, align: 'right' },
      { label: 'Remaining', x: right, align: 'right' },
    ],
    d.balances.map((b) => [b.label, String(b.entitlement), String(b.taken), String(b.remaining)]),
    'No entitlements recorded.',
  );

  miniTable(
    'Previous applications',
    [
      { label: 'Type', x: MX },
      { label: 'Dates', x: MX + 34 },
      { label: 'Days', x: right - 24, align: 'right' },
      { label: 'Status', x: right, align: 'right' },
    ],
    d.history.slice(0, 8).map((h) => [h.typeLabel, h.range, String(h.days), h.status]),
    'No previous applications.',
  );

  // --- signature block ---
  if (y > 178) { pdf.addPage(); y = 16; }
  y = Math.max(y + 4, 182);
  setD(RULE); pdf.line(MX, y, right, y); y += 6;
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8);
  pdf.text('HEAD OF SCHOOL — APPROVAL', MX, y); y += 9;

  setD(INK); pdf.setLineWidth(0.3);
  const colW = (right - MX - 6) / 2;
  pdf.line(MX, y, MX + colW, y);
  pdf.line(MX + colW + 6, y, right, y);
  setT(MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(6.5);
  pdf.text('Signature', MX, y + 3.5);
  pdf.text('Date', MX + colW + 6, y + 3.5);
  y += 10;
  setT(MUTED); pdf.setFontSize(6.5);
  pdf.text('Decision recorded in the system by the HR officer after signing.', MX, y);

  return pdf;
}
